import {
  assertMindmapCompositionsFit,
  type ClipboardConfigCreationContext,
  EdgelessClipboardConfigIdentifier,
  EdgelessCRUDIdentifier,
  type PreparedEdgelessBlock,
  snapshotMapKeys,
  SurfaceGroupLikeModel,
} from '@blocksuite/affine-block-surface';
import { toast } from '@blocksuite/affine-components/toast';
import {
  copyBlockIndexReviewSubject,
  edgelessToolbarI18nT,
  type EmbeddingIndexReviewSubjectKind,
} from '@blocksuite/affine-shared/services';
import { Bound, type IVec, type SerializedXYWH } from '@blocksuite/global/gfx';
import { assertType } from '@blocksuite/global/utils';
import type { BlockStdScope, Command } from '@blocksuite/std';
import {
  getStackingGroups,
  type GfxBlockElementModel,
  type GfxCompatibleProps,
  GfxControllerIdentifier,
  type GfxModel,
  type GfxPrimitiveElementModel,
  type SerializedElement,
  SortOrder,
} from '@blocksuite/std/gfx';
import {
  type BlockSnapshot,
  BlockSnapshotSchema,
  placeInsideCanvasWorld,
} from '@blocksuite/store';
import { PageStructureWriteError } from '@streamlain/database-identity';

import { createCanvasElement } from './canvas';
import {
  createNewPresentationIndexes,
  edgelessElementsBoundFromRawData,
  stripFileIndexSystemClipboardData,
} from './utils';

interface Input {
  elementsRawData: (SerializedElement | BlockSnapshot)[];
  pasteCenter?: IVec;
}

type CreatedElements = {
  canvasElements: GfxPrimitiveElementModel[];
  blockModels: GfxBlockElementModel[];
};

interface Output {
  createdElementsPromise: Promise<CreatedElements>;
}

export const createElementsFromClipboardDataCommand: Command<Input, Output> = (
  ctx,
  next
) => {
  const { std, elementsRawData } = ctx;
  const filteredElementsRawData =
    stripFileIndexSystemClipboardData(elementsRawData);
  let { pasteCenter } = ctx;

  const gfx = std.get(GfxControllerIdentifier);
  const toolManager = gfx.tool;

  const runner = async (): Promise<CreatedElements> => {
    let oldCommonBound, pasteX, pasteY;
    {
      const lastMousePos = toolManager.lastMousePos$.peek();
      pasteCenter = pasteCenter ?? [lastMousePos.x, lastMousePos.y];
      const [modelX, modelY] = pasteCenter;
      oldCommonBound = edgelessElementsBoundFromRawData(
        filteredElementsRawData
      );

      pasteX = modelX - oldCommonBound.w / 2;
      pasteY = modelY - oldCommonBound.h / 2;
      const placement = placeInsideCanvasWorld({
        x: pasteX,
        y: pasteY,
        w: oldCommonBound.w,
        h: oldCommonBound.h,
      });
      pasteX = placement.x;
      pasteY = placement.y;
    }

    const getNewXYWH = (oldXYWH: SerializedXYWH) => {
      const oldBound = Bound.deserialize(oldXYWH);
      return new Bound(
        oldBound.x + pasteX - oldCommonBound.x,
        oldBound.y + pasteY - oldCommonBound.y,
        oldBound.w,
        oldBound.h
      ).serialize();
    };

    const unitOf = new Map<string, string>();
    const firstOf = new Map<string, string>();
    const mindmaps = filteredElementsRawData.filter(
      (data): data is SerializedElement =>
        'type' in data && data.type === 'mindmap'
    );
    for (const map of mindmaps)
      for (const id of snapshotMapKeys(map.children)) unitOf.set(id, map.id);

    // All compositions are checked before anything is prepared, and again
    // right before the transaction writes them: the preparation awaits.
    const compositionsFit = () => {
      try {
        assertMindmapCompositionsFit(
          std.store,
          gfx.surface!.id,
          mindmaps.map(map => ({
            mindmap: { ...map, xywh: getNewXYWH(map.xywh) },
            members: filteredElementsRawData
              .filter(
                (data): data is SerializedElement =>
                  unitOf.get(data.id) === map.id
              )
              .map(data => ({ ...data, xywh: getNewXYWH(data.xywh) })),
          }))
        );
        return true;
      } catch (error) {
        if (
          !(error instanceof PageStructureWriteError) ||
          error.code !== 'page-part-unit-full'
        )
          throw error;
        toast(std.host, edgelessToolbarI18nT('mindmap.pagePartUnitFull'));
        return false;
      }
    };
    if (!compositionsFit()) return { canvasElements: [], blockModels: [] };

    // create blocks and canvas elements

    const context: ClipboardConfigCreationContext = {
      oldToNewIdMap: new Map<string, string>(),
      originalIndexes: new Map<string, string>(),
      newPresentationIndexes: await createNewPresentationIndexes(
        filteredElementsRawData,
        std
      ),
    };

    const blockModels: GfxBlockElementModel[] = [];
    const canvasElements: GfxPrimitiveElementModel[] = [];
    const allElements: GfxModel[] = [];

    const appendCanvasElement = (data: SerializedElement) => {
      const oldId = data.id;

      const element = createCanvasElement(
        std,
        data,
        context,
        getNewXYWH(data.xywh),
        firstOf.has(unitOf.get(oldId) ?? '')
          ? { partOf: firstOf.get(unitOf.get(oldId)!)! }
          : undefined
      );

      if (!element) return;
      const owner = unitOf.get(oldId);
      if (owner && !firstOf.has(owner)) firstOf.set(owner, element.id);

      canvasElements.push(element);
      allElements.push(element);

      context.oldToNewIdMap.set(oldId, element.id);
      context.originalIndexes.set(oldId, element.index);
    };

    const compositions = new Set<string>();
    for (const map of mindmaps) {
      for (const data of filteredElementsRawData)
        if (unitOf.get(data.id) === map.id) compositions.add(data.id);
      compositions.add(map.id);
    }

    // The asynchronous work of all blocks runs at once (asset checks,
    // snapshot conversion, database copies); nothing is written yet.
    type Step =
      | { kind: 'element'; data: SerializedElement }
      | {
          kind: 'block';
          oldId: string;
          originalIndex: string;
          prepared: Promise<PreparedEdgelessBlock | null>;
        };
    const steps: Step[] = [];
    for (const data of filteredElementsRawData) {
      if (compositions.has(data.id)) continue;
      const { data: blockSnapshot } = BlockSnapshotSchema.safeParse(data);
      if (!blockSnapshot) {
        assertType<SerializedElement>(data);
        steps.push({ kind: 'element', data });
        continue;
      }
      const oldId = blockSnapshot.id;

      const config = std.getOptional(
        EdgelessClipboardConfigIdentifier(blockSnapshot.flavour)
      );
      if (!config) continue;

      if (typeof blockSnapshot.props.index !== 'string') {
        console.error(`Block(id: ${oldId}) does not have index property`);
        continue;
      }
      const originalIndex = (blockSnapshot.props as GfxCompatibleProps).index;

      if (typeof blockSnapshot.props.xywh !== 'string') {
        console.error(`Block(id: ${oldId}) does not have xywh property`);
        continue;
      }

      assertType<GfxCompatibleProps>(blockSnapshot.props);

      blockSnapshot.props.xywh = getNewXYWH(
        blockSnapshot.props.xywh as SerializedXYWH
      );
      blockSnapshot.props.lockedBySelf = false;

      steps.push({
        kind: 'block',
        oldId,
        originalIndex,
        prepared: Promise.resolve(config.prepareBlock(blockSnapshot)),
      });
    }
    const results = await Promise.allSettled(
      steps.map(step => (step.kind === 'block' ? step.prepared : null))
    );
    const prepared = results.map(result =>
      result.status === 'fulfilled' ? result.value : null
    );
    // A prepared block that is not written settles all the same: that
    // releases what it reserved (a cut database) when the paste stops before
    // writing or fails.
    const unwritten = new Set(
      prepared.filter((block): block is PreparedEdgelessBlock => !!block)
    );

    const record = (newId: string) => {
      const block = std.store.getBlock(newId);
      if (!block) return;
      assertType<GfxBlockElementModel>(block.model);
      blockModels.push(block.model);
      allElements.push(block.model);
    };
    // One transaction writes everything: one update per page part instead of
    // one per block, and one undo step for the paste.
    const created: Array<{
      newId: string | null;
      settle?: () => Promise<string | null>;
    }> = [];
    try {
      const failed = results.find(result => result.status === 'rejected');
      if (failed) throw failed.reason;
      if (!compositionsFit()) return { canvasElements: [], blockModels: [] };
      std.store.transact(() => {
        // Mindmap compositions first, members before their mindmap.
        for (const map of mindmaps) {
          for (const data of filteredElementsRawData)
            if (unitOf.get(data.id) === map.id)
              appendCanvasElement(data as SerializedElement);
          appendCanvasElement(map);
        }
        steps.forEach((step, i) => {
          if (step.kind === 'element') {
            appendCanvasElement(step.data);
            return;
          }
          const block = prepared[i];
          if (!block) return;
          unwritten.delete(block);
          const newId = block.create(context);
          created.push({ newId, settle: block.settle });
          if (newId === null) return;
          context.oldToNewIdMap.set(step.oldId, newId);
          context.originalIndexes.set(step.oldId, step.originalIndex);
        });
      });
    } finally {
      await Promise.all([...unwritten].map(block => block.settle?.()));
    }
    // What is written after the transaction belongs to the step of the paste.
    const hold = std.store.history.holdStep();
    // The models exist once the transaction ended. One after the other: a
    // failed write is removed again, and what an owner inserts itself (a cut
    // database moves through the server) arrives.
    for (const { newId, settle } of created) {
      await settle?.();
      if (newId !== null) record(newId);
    }

    // remap old id to new id for the original index
    const oldIds = [...context.originalIndexes.keys()];
    oldIds.forEach(oldId => {
      const newId = context.oldToNewIdMap.get(oldId);
      const originalIndex = context.originalIndexes.get(oldId);
      if (newId && originalIndex) {
        context.originalIndexes.set(newId, originalIndex);
        context.originalIndexes.delete(oldId);
      }
    });

    hold(() =>
      updatePastedElementsIndex(std, allElements, context.originalIndexes)
    );
    copyPastedFileIndexReviewMetadata(
      std,
      filteredElementsRawData,
      context.oldToNewIdMap
    );

    return {
      canvasElements: canvasElements,
      blockModels: blockModels,
    };
  };

  return next({
    createdElementsPromise: runner(),
  });
};

type FileBlockReviewKind = Extract<
  EmbeddingIndexReviewSubjectKind,
  'attachment-block' | 'image-block'
>;

const FILE_BLOCK_REVIEW_KINDS: Partial<Record<string, FileBlockReviewKind>> = {
  'affine:attachment': 'attachment-block',
  'affine:image': 'image-block',
};

function copyPastedFileIndexReviewMetadata(
  std: BlockStdScope,
  elementsRawData: (SerializedElement | BlockSnapshot)[],
  oldToNewIdMap: Map<string, string>
) {
  for (const data of elementsRawData) {
    const { data: blockSnapshot } = BlockSnapshotSchema.safeParse(data);
    if (!blockSnapshot) continue;

    const subjectKind = FILE_BLOCK_REVIEW_KINDS[blockSnapshot.flavour];
    if (!subjectKind) continue;

    const oldId = blockSnapshot.id;
    const newId = oldToNewIdMap.get(oldId);
    const blobId = blockSnapshot.props.sourceId;
    if (!newId || typeof blobId !== 'string' || !blobId.trim()) continue;

    if (!std.store.getBlock(oldId)) continue;

    copyBlockIndexReviewSubject({
      std,
      subjectKind,
      sourceBlockId: oldId,
      targetBlockId: newId,
      blobId,
    });
  }
}

function updatePastedElementsIndex(
  std: BlockStdScope,
  elements: GfxModel[],
  originalIndexes: Map<string, string>
) {
  const gfx = std.get(GfxControllerIdentifier);
  const crud = std.get(EdgelessCRUDIdentifier);
  function compare(a: GfxModel, b: GfxModel) {
    if (a instanceof SurfaceGroupLikeModel && a.hasDescendant(b)) {
      return SortOrder.BEFORE;
    } else if (b instanceof SurfaceGroupLikeModel && b.hasDescendant(a)) {
      return SortOrder.AFTER;
    } else {
      const aGroups: GfxModel[] = getStackingGroups(a);
      const bGroups: GfxModel[] = getStackingGroups(b);

      let i = 1;
      let aGroup: GfxModel | undefined = aGroups.at(-i);
      let bGroup: GfxModel | undefined = bGroups.at(-i);

      while (aGroup === bGroup && aGroup) {
        ++i;
        aGroup = aGroups.at(-i);
        bGroup = bGroups.at(-i);
      }

      aGroup = aGroup ?? a;
      bGroup = bGroup ?? b;

      return originalIndexes.get(aGroup.id) === originalIndexes.get(bGroup.id)
        ? SortOrder.SAME
        : originalIndexes.get(aGroup.id)! < originalIndexes.get(bGroup.id)!
          ? SortOrder.BEFORE
          : SortOrder.AFTER;
    }
  }

  const idxGenerator = gfx.layer.createIndexGenerator();
  const sortedElements = elements.sort(compare);
  sortedElements.forEach(ele => {
    const newIndex = idxGenerator();

    crud.updateElement(ele.id, {
      index: newIndex,
    });
  });
}
